import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, AdminSchoolListItem, FormSubmission } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Building2,
  Users,
  GraduationCap,
  Search,
  Plus,
  Edit3,
  CheckCircle,
  XCircle,
  Shield,
  MessageSquare,
  Bug,
  Lightbulb,
  Clock,
  Eye,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(tab || "overview");

  useEffect(() => {
    if (tab) setActiveTab(tab);
  }, [tab]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate(value === "overview" ? "/admin" : `/admin/${value}`, { replace: true });
  };

  if (user?.role !== "admin") {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500 mt-2">Only platform admins can access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center">
          <Shield className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Platform Admin</h1>
          <p className="text-sm text-gray-500">Manage schools across the Ithopu platform</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="schools">Schools</TabsTrigger>
          <TabsTrigger value="create">Create School</TabsTrigger>
          <TabsTrigger value="feedback">Feedback</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab />
        </TabsContent>
        <TabsContent value="schools">
          <SchoolsTab />
        </TabsContent>
        <TabsContent value="create">
          <CreateSchoolTab onCreated={() => setActiveTab("schools")} />
        </TabsContent>
        <TabsContent value="feedback">
          <FeedbackTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ===== STAT CARD (reused from SchoolAdminDashboard pattern) =====
function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: number | string; color: string }) {
  const colorClasses: Record<string, string> = {
    blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
    green: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    purple: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
    orange: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
    red: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
    indigo: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400",
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            {icon}
          </div>
          <div>
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-sm text-gray-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ===== OVERVIEW TAB =====
function OverviewTab() {
  const [schools, setSchools] = useState<AdminSchoolListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await api.adminListSchools();
      setSchools(data.schools);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <p className="text-gray-500">Loading overview...</p>;

  const totalSchools = schools.length;
  const activeSchools = schools.filter((s) => s.is_active).length;
  const inactiveSchools = totalSchools - activeSchools;
  const totalStudents = schools.reduce((sum, s) => sum + s.student_count, 0);
  const totalTeachers = schools.reduce((sum, s) => sum + s.teacher_count, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <StatCard icon={<Building2 className="w-5 h-5" />} label="Total Schools" value={totalSchools} color="indigo" />
        <StatCard icon={<CheckCircle className="w-5 h-5" />} label="Active" value={activeSchools} color="green" />
        <StatCard icon={<XCircle className="w-5 h-5" />} label="Inactive" value={inactiveSchools} color="red" />
        <StatCard icon={<Users className="w-5 h-5" />} label="Total Students" value={totalStudents} color="blue" />
        <StatCard icon={<GraduationCap className="w-5 h-5" />} label="Total Teachers" value={totalTeachers} color="purple" />
      </div>

      {schools.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Schools</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {schools.slice(0, 5).map((s) => (
                <div key={s.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div>
                    <p className="font-medium">{s.name}</p>
                    <p className="text-sm text-gray-500">
                      {s.slug}.ithopu.com | {s.student_count} students, {s.teacher_count} teachers
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${s.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {s.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== SCHOOLS TAB =====
function SchoolsTab() {
  const [schools, setSchools] = useState<AdminSchoolListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingSchool, setEditingSchool] = useState<AdminSchoolListItem | null>(null);

  useEffect(() => {
    loadSchools();
  }, [search]);

  const loadSchools = async () => {
    setLoading(true);
    try {
      const data = await api.adminListSchools(search || undefined);
      setSchools(data.schools);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load schools");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (school: AdminSchoolListItem) => {
    try {
      await api.adminUpdateSchool({ school_id: school.id, is_active: !school.is_active });
      toast.success(`${school.name} ${school.is_active ? "deactivated" : "activated"}`);
      loadSchools();
    } catch (err: any) {
      toast.error(err?.message || "Failed to toggle status");
    }
  };

  return (
    <div className="space-y-6">
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          className="pl-10"
          placeholder="Search schools by name, slug, or city..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {editingSchool && (
        <EditSchoolForm
          school={editingSchool}
          onClose={() => setEditingSchool(null)}
          onSaved={() => { setEditingSchool(null); loadSchools(); }}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Schools ({schools.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : schools.length === 0 ? (
            <p className="text-gray-500">No schools found</p>
          ) : (
            <div className="space-y-3">
              {schools.map((s) => (
                <div key={s.id} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-lg">{s.name}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {s.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {s.slug}.ithopu.com
                        {s.city && ` | ${s.city}${s.state ? `, ${s.state}` : ""}`}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {s.student_count} students | {s.teacher_count} teachers | {s.classroom_count} classrooms
                        {s.admin_email && ` | Admin: ${s.admin_email}`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditingSchool(s)}>
                        <Edit3 className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant={s.is_active ? "outline" : "default"}
                        size="sm"
                        onClick={() => handleToggleActive(s)}
                      >
                        {s.is_active ? (
                          <><XCircle className="w-4 h-4 mr-1" /> Deactivate</>
                        ) : (
                          <><CheckCircle className="w-4 h-4 mr-1" /> Activate</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ===== EDIT SCHOOL FORM =====
function EditSchoolForm({
  school,
  onClose,
  onSaved,
}: {
  school: AdminSchoolListItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(school.name);
  const [contactEmail, setContactEmail] = useState(school.contact_email || "");
  const [city, setCity] = useState(school.city || "");
  const [state, setState] = useState(school.state || "");
  const [country, setCountry] = useState(school.country || "");
  const [maxTeachers, setMaxTeachers] = useState(school.max_teachers);
  const [maxStudents, setMaxStudents] = useState(school.max_students);
  const [adminEmail, setAdminEmail] = useState(school.admin_email || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.adminUpdateSchool({
        school_id: school.id,
        name,
        contact_email: contactEmail || undefined,
        city: city || undefined,
        state: state || undefined,
        country: country || undefined,
        max_teachers: maxTeachers,
        max_students: maxStudents,
        admin_email: adminEmail !== (school.admin_email || "") ? adminEmail || undefined : undefined,
      });
      toast.success("School updated");
      onSaved();
    } catch (err: any) {
      toast.error(err?.message || "Failed to update school");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-indigo-300">
      <CardHeader>
        <CardTitle className="text-lg">Edit: {school.name}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>School Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Subdomain</Label>
            <Input value={`${school.slug}.ithopu.com`} disabled className="bg-gray-100" />
          </div>
          <div className="space-y-2">
            <Label>Contact Email</Label>
            <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>School Admin Email</Label>
            <Input
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="Assign school admin by email"
            />
          </div>
          <div className="space-y-2">
            <Label>City</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>State</Label>
            <Input value={state} onChange={(e) => setState(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Country</Label>
            <Input value={country} onChange={(e) => setCountry(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Max Teachers</Label>
            <Input type="number" value={maxTeachers} onChange={(e) => setMaxTeachers(Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Max Students</Label>
            <Input type="number" value={maxStudents} onChange={(e) => setMaxStudents(Number(e.target.value))} />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ===== CREATE SCHOOL TAB =====
function CreateSchoolTab({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [maxTeachers, setMaxTeachers] = useState(50);
  const [maxStudents, setMaxStudents] = useState(1000);
  const [academicYear, setAcademicYear] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [creating, setCreating] = useState(false);

  // Auto-generate slug from name
  const handleNameChange = (value: string) => {
    setName(value);
    if (!slug || slug === generateSlug(name)) {
      setSlug(generateSlug(value));
    }
  };

  const generateSlug = (value: string) =>
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 32);

  const handleCreate = async () => {
    if (!name.trim() || !slug.trim()) {
      toast.error("Name and slug are required");
      return;
    }
    setCreating(true);
    try {
      await api.adminCreateSchool({
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || undefined,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        country: country.trim() || undefined,
        contact_email: contactEmail.trim() || undefined,
        contact_phone: contactPhone.trim() || undefined,
        max_teachers: maxTeachers,
        max_students: maxStudents,
        academic_year: academicYear.trim() || undefined,
        admin_email: adminEmail.trim() || undefined,
      });
      toast.success("School created successfully!");
      // Reset form
      setName(""); setSlug(""); setDescription(""); setAddress("");
      setCity(""); setState(""); setCountry(""); setContactEmail("");
      setContactPhone(""); setMaxTeachers(50); setMaxStudents(1000);
      setAcademicYear(""); setAdminEmail("");
      onCreated();
    } catch (err: any) {
      toast.error(err?.message || "Failed to create school");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Plus className="w-5 h-5" />
          Create New School
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>School Name *</Label>
            <Input value={name} onChange={(e) => handleNameChange(e.target.value)} placeholder="e.g., Springfield Academy" />
          </div>
          <div className="space-y-2">
            <Label>Subdomain (slug) *</Label>
            <div className="flex items-center gap-1">
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g., springfield-academy" />
              <span className="text-sm text-gray-500 whitespace-nowrap">.ithopu.com</span>
            </div>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Description</Label>
            <textarea
              className="w-full border rounded-lg p-3 text-sm dark:bg-gray-800 dark:border-gray-700"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the school"
            />
          </div>
          <div className="space-y-2">
            <Label>Address</Label>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>City</Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>State</Label>
            <Input value={state} onChange={(e) => setState(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Country</Label>
            <Input value={country} onChange={(e) => setCountry(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Contact Email</Label>
            <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Contact Phone</Label>
            <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Max Teachers</Label>
            <Input type="number" value={maxTeachers} onChange={(e) => setMaxTeachers(Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Max Students</Label>
            <Input type="number" value={maxStudents} onChange={(e) => setMaxStudents(Number(e.target.value))} />
          </div>
          <div className="space-y-2">
            <Label>Academic Year</Label>
            <Input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} placeholder="e.g., 2025-2026" />
          </div>
          <div className="space-y-2">
            <Label>School Admin Email (optional)</Label>
            <Input
              type="email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              placeholder="Assign an existing user as school admin"
            />
            <p className="text-xs text-gray-500">User must have an existing Ithopu account</p>
          </div>
        </div>
        <Button className="mt-6" onClick={handleCreate} disabled={creating}>
          {creating ? "Creating..." : "Create School"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ===== FEEDBACK TAB =====
const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-800" },
  reviewed: { label: "Reviewed", color: "bg-purple-100 text-purple-800" },
  resolved: { label: "Resolved", color: "bg-green-100 text-green-800" },
  archived: { label: "Archived", color: "bg-gray-100 text-gray-800" },
};

const TYPE_LABELS: Record<string, { label: string; icon: typeof Bug }> = {
  bug_report: { label: "Bug Report", icon: Bug },
  feature_request: { label: "Enhancement", icon: Lightbulb },
  feedback: { label: "Feedback", icon: MessageSquare },
  beta_testing: { label: "Beta Testing", icon: Users },
  content_submission: { label: "Content", icon: Edit3 },
  partnership_inquiry: { label: "Partnership", icon: Building2 },
};

function FeedbackTab() {
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    loadSubmissions();
  }, [filterType, filterStatus, search]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const filters: { form_type?: string; status?: string; search?: string } = {};
      if (filterType !== "all") filters.form_type = filterType;
      if (filterStatus !== "all") filters.status = filterStatus;
      if (search.trim()) filters.search = search.trim();
      const data = await api.adminListFeedback(filters);
      setSubmissions(data.submissions);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (submissionId: string, newStatus: string) => {
    try {
      await api.adminUpdateFeedback({ submission_id: submissionId, status: newStatus });
      toast.success("Status updated");
      setSubmissions((prev) =>
        prev.map((s) => (s.id === submissionId ? { ...s, status: newStatus as FormSubmission["status"] } : s))
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to update status");
    }
  };

  const handleNotesUpdate = async (submissionId: string, notes: string) => {
    try {
      await api.adminUpdateFeedback({ submission_id: submissionId, admin_notes: notes });
      toast.success("Notes saved");
      setSubmissions((prev) =>
        prev.map((s) => (s.id === submissionId ? { ...s, admin_notes: notes } : s))
      );
    } catch (err: any) {
      toast.error(err?.message || "Failed to save notes");
    }
  };

  // Counts for stat cards
  const totalBugs = submissions.filter((s) => s.form_type === "bug_report").length;
  const totalEnhancements = submissions.filter((s) => s.form_type === "feature_request").length;
  const totalPending = submissions.filter((s) => s.status === "pending").length;
  const totalInProgress = submissions.filter((s) => s.status === "in_progress").length;

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={<Bug className="w-5 h-5" />} label="Bug Reports" value={totalBugs} color="red" />
        <StatCard icon={<Lightbulb className="w-5 h-5" />} label="Enhancements" value={totalEnhancements} color="orange" />
        <StatCard icon={<Clock className="w-5 h-5" />} label="Pending" value={totalPending} color="blue" />
        <StatCard icon={<Eye className="w-5 h-5" />} label="In Progress" value={totalInProgress} color="purple" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-10"
            placeholder="Search by email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="bug_report">Bug Reports</SelectItem>
            <SelectItem value="feature_request">Enhancements</SelectItem>
            <SelectItem value="feedback">Feedback</SelectItem>
            <SelectItem value="beta_testing">Beta Testing</SelectItem>
            <SelectItem value="content_submission">Content</SelectItem>
            <SelectItem value="partnership_inquiry">Partnership</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Submissions List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Submissions ({submissions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : submissions.length === 0 ? (
            <p className="text-gray-500">No submissions found</p>
          ) : (
            <div className="space-y-3">
              {submissions.map((s) => (
                <FeedbackItem
                  key={s.id}
                  submission={s}
                  isExpanded={expandedId === s.id}
                  onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
                  onStatusChange={handleStatusChange}
                  onNotesUpdate={handleNotesUpdate}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function FeedbackItem({
  submission,
  isExpanded,
  onToggle,
  onStatusChange,
  onNotesUpdate,
}: {
  submission: FormSubmission;
  isExpanded: boolean;
  onToggle: () => void;
  onStatusChange: (id: string, status: string) => void;
  onNotesUpdate: (id: string, notes: string) => void;
}) {
  const [notes, setNotes] = useState(submission.admin_notes || "");
  const typeInfo = TYPE_LABELS[submission.form_type] || { label: submission.form_type, icon: MessageSquare };
  const statusInfo = STATUS_LABELS[submission.status] || { label: submission.status, color: "bg-gray-100 text-gray-800" };
  const TypeIcon = typeInfo.icon;

  const title = submission.form_data?.title || submission.form_data?.message || "No title";
  const description = submission.form_data?.description || "";
  const severity = submission.form_data?.severity || submission.form_data?.priority || "";

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header row */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex-shrink-0">
          <TypeIcon className="w-5 h-5 text-gray-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-sm truncate">{title}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusInfo.color}`}>
              {statusInfo.label}
            </span>
            {severity && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                severity === "critical" ? "bg-red-100 text-red-800" :
                severity === "high" ? "bg-orange-100 text-orange-800" :
                severity === "medium" ? "bg-yellow-100 text-yellow-800" :
                "bg-gray-100 text-gray-600"
              }`}>
                {severity}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {typeInfo.label} &middot; {submission.email} &middot; {new Date(submission.created_at).toLocaleDateString()}
          </p>
        </div>
        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {/* Expanded details */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-3 space-y-4">
          {/* Description */}
          {description && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Description</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{description}</p>
            </div>
          )}

          {/* Additional fields from form_data */}
          {submission.form_data?.steps_to_reproduce && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">Steps to Reproduce</p>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{submission.form_data.steps_to_reproduce}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {submission.form_data?.expected_behavior && (
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">Expected Behavior</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">{submission.form_data.expected_behavior}</p>
              </div>
            )}
            {submission.form_data?.actual_behavior && (
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">Actual Behavior</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">{submission.form_data.actual_behavior}</p>
              </div>
            )}
          </div>

          {/* Technical info */}
          {(submission.form_data?.page_url || submission.form_data?.browser) && (
            <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded text-xs space-y-1">
              {submission.form_data.page_url && (
                <p><span className="font-semibold text-gray-500">Page:</span> {submission.form_data.page_url}</p>
              )}
              {submission.form_data.os && (
                <p><span className="font-semibold text-gray-500">OS:</span> {submission.form_data.os}</p>
              )}
            </div>
          )}

          {/* Status change */}
          <div className="flex items-center gap-3">
            <Label className="text-xs whitespace-nowrap">Set Status:</Label>
            <Select
              value={submission.status}
              onValueChange={(val) => onStatusChange(submission.id, val)}
            >
              <SelectTrigger className="w-[160px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Admin notes */}
          <div className="space-y-2">
            <Label className="text-xs">Admin Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add internal notes..."
              rows={2}
              className="text-sm resize-none"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => onNotesUpdate(submission.id, notes)}
              disabled={notes === (submission.admin_notes || "")}
            >
              Save Notes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
